import { Group, Skeleton, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { formatCurrency } from '../../../helpers/financial/financial';
import { useFinancialsStore } from '../../../store/financials-store/financials-store';
import type { CostVersusOutputMeasures } from '../../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { newCOLORS } from '../../../styles/colors';
import { Icon } from '../../../ui-library/icon/icon';
import { useCostVersusOutput } from '../financials.hooks';
import {
  buildTrendLines,
  measureColorMap,
  measureNameMap,
  transformCostVersusOutputData,
} from './cost-versus-output/cost-versus-output.helpers';
import type { DataPoint } from './overview.type';
import { CustomTooltip } from './shared/custom-tooltip';

function Overview() {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const startDate = useFinancialsStore((state) => state.startDate);
  const endDate = useFinancialsStore((state) => state.endDate);

  const { data, query } = useCostVersusOutput(
    dayjs(startDate).toDate(),
    dayjs(endDate).toDate(),
    portfolio?.id || null,
    null,
    null,
  );
  const chartData = transformCostVersusOutputData(data);

  const chartDataWithTrendLines = buildTrendLines(chartData);

  return (
    <div style={{ display: 'flex', gap: 16 }}>
      {query.isPending ? (
        <>
          <Skeleton height={200} radius={10} />
          <Skeleton height={200} radius={10} />
          <Skeleton height={200} radius={10} />
          <Skeleton height={200} radius={10} />
          <Skeleton height={200} radius={10} />
        </>
      ) : (
        <>
          <OverviewCard
            chartData={chartDataWithTrendLines}
            dateKey="cost"
            color={measureColorMap.cost}
            iconName="payments"
            label="Cost"
          />
          <OverviewCard
            chartData={chartDataWithTrendLines}
            dateKey="cost_per_task"
            color={measureColorMap.cost_per_task}
            iconName="payments"
            label="Cost per Task"
          />
          <OverviewCard
            chartData={chartDataWithTrendLines}
            dateKey="daily_burn"
            color={measureColorMap.daily_burn}
            iconName="stacked_line_chart"
            label="Daily Spend"
          />
          <OverviewCard
            chartData={chartDataWithTrendLines}
            dateKey="completed_tasks"
            color={measureColorMap.completed_tasks}
            iconName="switch_access_shortcut"
            label="Throughput"
          />
          <OverviewCard
            chartData={chartDataWithTrendLines}
            dateKey="committed_tasks"
            color={measureColorMap.committed_tasks}
            iconName="add_task"
            label="Tasks Committed"
          />
        </>
      )}
    </div>
  );
}

function OverviewCard({
  chartData,
  dateKey,
  color,
  iconName,
  label,
}: {
  chartData: DataPoint[];
  dateKey: CostVersusOutputMeasures;
  color: string;
  iconName: string;
  label: string;
}) {
  return (
    <div
      style={{
        flex: 1,
        backgroundColor: newCOLORS.lighterGray,
        borderRadius: 10,
        border: `2px solid ${newCOLORS.lightGray2}`,
        maxHeight: 200,
      }}
    >
      <Group gap={4} pl={16} pt={16}>
        <Icon name={iconName} size={24} color={newCOLORS.purple} />
        <Text fw={600}>{label}</Text>
      </Group>
      <ResponsiveContainer width="100%" height={150}>
        <LineChart data={chartData} margin={{ top: 16, right: 16, left: 16, bottom: 32 }}>
          <Tooltip
            content={
              <CustomTooltip
                nameFormatter={(item) => measureNameMap[item.dataKey as keyof typeof measureNameMap]}
                valueFormatter={(item) => formatCurrency(Number(item.value))}
                filterFn={(item) => Boolean(item.dataKey && String(item.dataKey) in measureNameMap)}
              />
            }
          />
          <XAxis dataKey="date" hide={true} />
          <Line type="natural" dataKey={dateKey} stroke={color} strokeWidth={2} dot={false} />
          <Line
            type="natural"
            dataKey={`${dateKey}_trend`}
            stroke={color}
            strokeWidth={1}
            strokeDasharray="5 5"
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export { Overview };
